import { reflect } from '@effector/reflect';
import { Button, NumberInput, TextInput } from '@mantine/core';
import dayjs from 'dayjs';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { equals, not } from 'patronum';
import { createBlockVoucherModal } from 'features/block-voucher';
import { createDeleteVoucherModal } from 'features/delete-voucher';
import { createUnblockVoucherModal } from 'features/unblock-voucher';
import { viewerModel } from 'entities/viewer';
import { USER_ROLES } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { voucherPageModel } from '../../model';
import cls from './voucher-page-info.module.scss';

const BlockVoucherModal = createBlockVoucherModal(voucherPageModel.blockVoucherModel);

const UnblockVoucherModal = createUnblockVoucherModal(voucherPageModel.unblockVoucherModel);

const DeleteVoucherModal = createDeleteVoucherModal(voucherPageModel.deleteVoucherModel);

const CurrencyField = reflect({
	view: TextInput,
	bind: {
		value: voucherPageModel.$voucher.map((voucher) => voucher?.currency ?? ''),
		label: translated('voucher-page.currency'),
		readOnly: true,
	},
});

const CurrentCountField = reflect({
	view: NumberInput,
	bind: {
		value: voucherPageModel.$voucher.map((voucher) => voucher?.currentCount ?? ''),
		label: translated('voucher-page.current-count'),
		readOnly: true,
	},
});

const TotalCountField = reflect({
	view: NumberInput,
	bind: {
		value: voucherPageModel.$voucher.map((voucher) => voucher?.totalCount ?? ''),
		label: translated('voucher-page.total-count'),
		readOnly: true,
	},
});

const DepositField = reflect({
	view: NumberInput,
	bind: {
		required: true,
		decimalScale: 2,
		rightSection: voucherPageModel.$voucher.map((voucher) => voucher?.currency ?? null),
		'data-test-id': 'voucher-page-deposit-input',
		label: translated('voucher-page.deposit'),
		placeholder: translated('voucher-page.deposit-placeholder'),
		value: voucherPageModel.form.fields.deposit.$value,
		onChange: (value) => voucherPageModel.form.fields.deposit.change(String(value ?? '')),
		readOnly: not(equals(viewerModel.$role, USER_ROLES.god)),
		errorProps: { 'data-test-id': 'voucher-page-deposit-input-error' },
		error: combine(
			[voucherPageModel.form.fields.deposit.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('voucher-page.deposit'),
				}),
		),
	},
});

const WageringField = reflect({
	view: NumberInput,
	bind: {
		required: true,
		decimalScale: 2,
		rightSection: voucherPageModel.$voucher.map((voucher) => voucher?.currency ?? null),
		'data-test-id': 'voucher-page-wagering-input',
		label: translated('voucher-page.wagering'),
		placeholder: translated('voucher-page.wagering-placeholder'),
		value: voucherPageModel.form.fields.wagering.$value,
		onChange: (value) => voucherPageModel.form.fields.wagering.change(String(value ?? '')),
		readOnly: not(equals(viewerModel.$role, USER_ROLES.god)),
		errorProps: { 'data-test-id': 'voucher-page-wagering-input-error' },
		error: combine(
			[voucherPageModel.form.fields.wagering.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('voucher-page.wagering'),
				}),
		),
	},
});

const CreatedAtField = reflect({
	view: TextInput,
	bind: {
		value: voucherPageModel.$voucher.map(
			(voucher) => dayjs(voucher?.createdAt).format('YYYY-MM-DD HH:mm:ss') ?? '',
		),
		label: translated('voucher-page.created-at'),
		readOnly: true,
	},
});

const SaveButton = reflect({
	view: Button,
	bind: {
		type: 'submit',
		children: translated('voucher-page.save'),
		loading: voucherPageModel.editVoucherMutation.$pending,
		disabled: combine([voucherPageModel.form.$pristine, viewerModel.$role]).map(
			([pristine, role]) => pristine || role !== USER_ROLES.god,
		),
		className: cls.voucherPageInfoFooterOption,
	},
});

export const VoucherPageInfo = () => {
	const [voucher, submit] = useUnit([voucherPageModel.$voucher, voucherPageModel.form.submit]);

	if (voucher === null) {
		return null;
	}

	return (
		<>
			<form
				className={cls.voucherPageInfo}
				onSubmit={(event) => {
					event.preventDefault();
					submit();
				}}
			>
				<CurrencyField />
				<CurrentCountField />
				<TotalCountField />
				<DepositField />
				<WageringField />
				<CreatedAtField />
				<div className={cls.voucherPageInfoFooter}>
					<SaveButton />
					<div className={cls.voucherPageInfoFooterOptions}>
						{voucher.isLocked ? (
							<UnblockVoucherModal.Button
								modalData={voucher}
								className={cls.voucherPageInfoFooterOption}
							/>
						) : (
							<BlockVoucherModal.Button
								modalData={voucher}
								className={cls.voucherPageInfoFooterOption}
							/>
						)}
						<DeleteVoucherModal.Button
							modalData={voucher}
							disabled={voucher.currentCount < voucher.totalCount}
							className={cls.voucherPageInfoFooterOption}
						/>
					</div>
				</div>
			</form>
			<BlockVoucherModal />
			<UnblockVoucherModal />
			<DeleteVoucherModal />
		</>
	);
};
